import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { CardWrapper } from '@components/shared/CardWrapper'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './components/RegistrationDataForm/form/initialFormValues'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { parseProfitCenterGrid } from './helpers/parseProfitCenterGrid'
import { RegistrationDataForm } from './components/RegistrationDataForm'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { setInitialValues } from './components/RegistrationDataForm/form/setInitialValues'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { useMediaQuery } from '@mui/material'
import { UserDetailsModal } from './components/UserDetailsModal'
import { useUsers } from '@pages/Users/context'
import { ClFilters } from './components/ClFilters'

import type { UserDataModalProps } from './types'
import type { UserDataFormProps } from './components/RegistrationDataForm/form/types'
import type { SortProps } from '@components/shared/Table/types'
import type { ClByUserCodeItemProps } from '@pages/Users/context/types'

export const UserDataModal = ({ open, onClose, data, ...rest }: UserDataModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const {
    usersData,
    clByUserCodeData,
    isClByUserCodeLoading,
    getClByUserCode,
    saveUserProfile,
    isSaveUserProfileLoading
  } = useUsers()

  const [clDetailsData, setClDetailsData] = useState<ClByUserCodeItemProps | null>(null)
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false)
  const [clByUserCodeSortData, setClByUserCodeSortData] = useState<SortProps | null>(null)
  const [clByUserCodeFormData, setClByUserCodeFormData] = useState<ClByUserCodeItemProps | null>(null)

  const methods = useForm<UserDataFormProps>({
    defaultValues: initialFormValues
  })

  const onError = (errors: FieldErrors<UserDataFormProps>) => console.log({ errors })

  const onSubmit = (formData: UserDataFormProps) => {
    formData.id = data?.id!

    saveUserProfile(formData)
  }

  const handleGetClDetails = (data: ClByUserCodeItemProps) => {
    setOpenUserDetailsModal(true)
    setClDetailsData(data)
  }

  const handleGetClByUserCode = (
    params: PaginationParams | null,
    formData?: ClByUserCodeItemProps | null,
    sortData?: SortProps
  ) => {
    getClByUserCode({
      ...(sortData || clByUserCodeSortData),
      ...(formData || clByUserCodeFormData)!,
      userCode: data?.code!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || clByUserCodeData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setClByUserCodeFormData(formData)
    !!sortData && setClByUserCodeSortData(sortData)
  }

  const handleInitialActionsFromScreen = () => {
    handleGetClByUserCode(null)
  }

  const handleSetInitialValues = () => {
    methods.reset(setInitialValues(data, usersData)!)
  }

  useEffect(() => {
    if (!!data && open) {
      handleSetInitialValues()
      handleInitialActionsFromScreen()
    }
  }, [data, open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
        heightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        headerIcon={<Breadcrumb breadcrumbData={[{ label: 'Usuários' }, { label: data?.name || '' }]} />}
        onClose={onClose}
        footer={{
          cancelButtonText: 'Cancelar',
          confirmButtonText: 'Enviar',
          onConfirm: () => methods.handleSubmit(onSubmit, onError)()
        }}
        loading={isSaveUserProfileLoading}
        {...rest}
      >
        <Title label="Dados Cadastrais" marginTop={0} />

        <CardWrapper>
          <FormProvider {...methods}>
            <RegistrationDataForm data={data} />
          </FormProvider>
        </CardWrapper>

        <Title label="Centro de Lucro" marginBottom={0} />

        <ClFilters onFilter={(formData) => handleGetClByUserCode(null, formData as unknown as ClByUserCodeItemProps)} />

        <Table
          data={parseProfitCenterGrid({
            data: clByUserCodeData?.items || [],
            onSeeMore: handleGetClDetails
          })}
          fixedRow={{
            element: <RowTableTotal2 data={[{ label: 'Total Registros', value: clByUserCodeData?.totalItems || 0 }]} />
          }}
          footer={{
            pagination: {
              currentPage: clByUserCodeData?.currentPage,
              itemsPerPage: clByUserCodeData?.itemsPerPage,
              totalItems: clByUserCodeData?.totalItems,
              totalPages: clByUserCodeData?.totalPages,
              onPageChange: (page) => handleGetClByUserCode({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetClByUserCode({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetClByUserCode(null, null, sortData)}
          isLoading={isClByUserCodeLoading}
        />
      </BaseModal2>

      <UserDetailsModal
        userData={data}
        data={clDetailsData}
        open={openUserDetailsModal}
        onClose={setOpenUserDetailsModal}
      />
    </>
  )
}
